import { type FC, Suspense, useState, useEffect, lazy } from 'react';
import { graphql, useQueryLoader } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import Dropdown, { alignConstants, sizeConstants } from '../../Dropdown/Dropdown.tsx';
import DropdownBody from '../../Dropdown/DropdownBody.tsx';
import DropdownLoadingContent from '../../Dropdown/DropdownLoadingContent.tsx';
import { UserNotificationsIcon } from './UserNotificationsIcon.tsx';
import { UserNotificationsMarkAllAsRead } from './UserNotificationsMarkAllAsRead.tsx';

import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { getPriceBookName } from 'dibs-cookie-jar';
import { stepInteractionConstants } from 'dibs-tracking';
import { trackGlobalNavEvent } from './userNotificationsTracking.ts';

import { type UserNotificationsQuery } from './__generated__/UserNotificationsQuery.graphql.ts';
import { type UserNotificationsTabsQuery } from './__generated__/UserNotificationsTabsQuery.graphql.ts';

import styles from './UserNotifications.scss';
import dibsCss from 'dibs-css';

export type UnreadNotificationsIds = {
    activityFeedIds: string[];
    itemEventIds: string[];
    conversationIds: string[];
};

export type SetUnreadNotifications = (payload: Partial<UnreadNotificationsIds>) => void;

const UserNotificationsTabs = lazy(
    () => import(/* webpackChunkName: "UserNotificationsTabsLazy" */ './UserNotificationsTabs.tsx')
);

const MobileNotificationsModal = lazy(
    () =>
        import(
            /* webpackChunkName: "MobileNotificationsModalLazy" */ '../MobileNotificationsModal.tsx'
        )
);

export const userNotificationsQuery = graphql`
    query UserNotificationsQuery($userId: String!) {
        viewer {
            ...UserNotificationsMarkAllAsRead_viewer
            ...UserNotificationsIcon_viewer
        }
    }
`;

export const userNotificationsTabsQuery = graphql`
    query UserNotificationsTabsQuery(
        $userId: String!
        $hasTradeUserId: Boolean!
        $userCountryCode: String!
        $priceBookName: String!
    ) {
        viewer {
            ...UserNotificationsTabs_viewer
        }
    }
`;

const noop = (): null => null;

const UserNotifications: FC<{
    userId: string;
    hasTradeUserId: boolean;
    isMobile?: boolean;
    hasRootPendingRefetch: boolean;
}> = ({ userId, hasTradeUserId, isMobile, hasRootPendingRefetch }) => {
    const [showNotifications, setShowNotifications] = useState(false);
    const [hasSeenNotifications, setHasSeenNotifications] = useState(false);

    const [notificationsQueryRef, loadNotificationsQuery] =
        useQueryLoader<UserNotificationsQuery>(userNotificationsQuery);

    const [notificationsTabsQueryRef, loadNotificationsTabsQuery] =
        useQueryLoader<UserNotificationsTabsQuery>(userNotificationsTabsQuery);

    useEffect(() => {
        if (hasRootPendingRefetch) {
            return;
        }

        loadNotificationsQuery({
            userId,
        });
    }, [userId, loadNotificationsQuery, hasTradeUserId, hasRootPendingRefetch]);

    useEffect(() => {
        if (notificationsQueryRef) {
            loadNotificationsTabsQuery({
                userId,
                userCountryCode: getUserSessionCountryCode(),
                priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
                hasTradeUserId,
            });
        }
    }, [notificationsQueryRef, loadNotificationsTabsQuery, userId, hasTradeUserId]);

    const trackNotificationsDisplayed = (): void => {
        trackGlobalNavEvent({
            stepInteractionName:
                stepInteractionConstants.STEP_INTERACTION_GLOBAL_NAV_NOTIFICATIONS_CLICK,
            trigger: 'notification icon hover or click',
        });
    };

    const onShowNotifications = (): void => {
        if (showNotifications) {
            return;
        }

        setShowNotifications(true);
        trackNotificationsDisplayed();
    };

    const onToggleNotifications = (): void => {
        setShowNotifications(!showNotifications);
        trackNotificationsDisplayed();
    };

    const notificationsContent = (
        <>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyBetween,
                    dibsCss.pxSmall,
                    dibsCss.ptSmall,
                    { [styles.mobileHeader]: isMobile }
                )}
            >
                <HeadingLevel>
                    {Heading => (
                        <Heading className={classnames(dibsCss.m0, dibsCss.textBodyLargeHeavy)}>
                            <FormattedMessage
                                id="dbl.UserNotifications.title"
                                defaultMessage="Notifications"
                            />
                        </Heading>
                    )}
                </HeadingLevel>
                <Suspense fallback={null}>
                    {notificationsQueryRef && (
                        <UserNotificationsMarkAllAsRead
                            queryRef={notificationsQueryRef}
                            userId={userId}
                            hasTradeUserId={hasTradeUserId}
                        />
                    )}
                </Suspense>
            </div>
            {!notificationsTabsQueryRef && <DropdownLoadingContent />}
            {notificationsTabsQueryRef && (
                <Suspense fallback={<DropdownLoadingContent />}>
                    <UserNotificationsTabs
                        queryRef={notificationsTabsQueryRef}
                        hasTradeUserId={hasTradeUserId}
                        userId={userId}
                        hasSeenNotifications={hasSeenNotifications}
                        setHasSeenNotifications={() => setHasSeenNotifications(true)}
                    />
                </Suspense>
            )}
        </>
    );

    return (
        <ErrorBoundary fallback={noop}>
            {isMobile ? (
                <>
                    <UserNotificationsIcon
                        isMobile
                        queryRef={notificationsQueryRef}
                        onClick={onShowNotifications}
                        isOpen={showNotifications}
                    />
                    {showNotifications && (
                        <Suspense fallback={null}>
                            <MobileNotificationsModal onClose={() => setShowNotifications(false)}>
                                {notificationsContent}
                            </MobileNotificationsModal>
                        </Suspense>
                    )}
                </>
            ) : (
                <div data-tn="account-nav" className={classnames(dibsCss.flex, dibsCss.hFull)}>
                    <Dropdown
                        align={alignConstants.ALIGN_RIGHT}
                        size={sizeConstants.SIZE_MEDIUM_LARGE}
                        onShow={onShowNotifications}
                        onHide={() => setShowNotifications(false)}
                        dropdownClassName={styles.dropdown}
                        forceOpen={showNotifications}
                    >
                        <UserNotificationsIcon
                            isOpen={showNotifications}
                            queryRef={notificationsQueryRef}
                            onClick={onToggleNotifications}
                        />
                        {showNotifications && <DropdownBody>{notificationsContent}</DropdownBody>}
                    </Dropdown>
                </div>
            )}
        </ErrorBoundary>
    );
};

export { UserNotifications };
